import { useState, useEffect, useContext } from "react";
import UserContext from "../../../context/UserContext";
import {
    formatDateYYYMMDD,
    daysUntilExpire,
    addDaysToDate,
    getLatestDate,
} from "../../../util/dateUtil";
import Payment from "../../../util/Payment";
import SlotApi from "../../../api/SlotApi";
import { loginAlert } from "../../common/CustomAlert";

const OPTION_TABS = [
    {
        id: "increase",
        label: (
            <>
                <span className="pc-only">순위</span> 조회 슬롯 늘리기
            </>
        ),
    },
    {
        id: "decrease",
        label: (
            <>
                <span className="pc-only">순위</span> 조회 슬롯 줄이기
            </>
        ),
    },
];

const SLOT_OPTIONS = [
    { prodId: 10050, value: 50, count: "50", price: 9900, priceLabel: "9,900원" },
    { prodId: 10100, value: 100, count: "100", price: 15900, priceLabel: "15,900원" },
    { prodId: 10200, value: 200, count: "200", price: 20900, priceLabel: "20,900원" },
    { prodId: 10300, value: 300, count: "300", price: 25900, priceLabel: "25,900원" },
    { prodId: 10500, value: 500, count: "500", price: 45000, priceLabel: "45,000원" },
    { prodId: 11000, value: 1000, count: "1000", price: 79900, priceLabel: "79,900원" },
];

const EXTEND_OPTIONS = [
    { prodId: 20000, value: 0, label: "연장안함" },
    { prodId: 20030, value: 30, label: "30일 연장" },
    { prodId: 20060, value: 60, label: "60일 연장" },
    { prodId: 20090, value: 90, label: "90일 연장" },
];

const getSlotOptions = (userSlotCnt, isIncrease = true) => {
    return SLOT_OPTIONS.map((option) => {
        let disabled = option.value < userSlotCnt;
        if (!isIncrease) disabled = !disabled;
        return { ...option, disabled };
    });
};

const getDefaultSlotOption = (curCnt, isIncrease = true) => {
    if (!isIncrease) {
        const enabled = SLOT_OPTIONS.filter((o) => o.value < curCnt);
        return enabled.length ? enabled[enabled.length - 1] : SLOT_OPTIONS[0];
    }
    if (curCnt <= SLOT_OPTIONS[0].value) return SLOT_OPTIONS[0];
    return SLOT_OPTIONS.find((slot) => slot.value === curCnt) || SLOT_OPTIONS[0];
};

const getDefaultExtendOption = (expireDate) => {
    if (!expireDate || expireDate < new Date()) return EXTEND_OPTIONS[1];
    return EXTEND_OPTIONS[0];
};

const rankTrackDisabledStyles = `
    .membership-rank-track .membership-option-item.is-disabled {
        pointer-events: none;
    }
    .membership-rank-track .membership-option-item.is-disabled .membership-option-box {
        cursor: not-allowed;
        background-color: #f7f7f7;
        border-color: #e8e8e8;
        opacity: 0.55;
    }
    .membership-rank-track .membership-option-item.is-disabled .membership-option-box span,
    .membership-rank-track .membership-option-item.is-disabled .membership-option-box strong {
        color: #aaa;
    }
`;

const MembershipRankTrack = () => {
    const { user, updateUserInfo } = useContext(UserContext);
    const [activeOptionTab, setActiveOptionTab] = useState(OPTION_TABS[0].id);
    const [curSlotOption, setCurSlotOption] = useState(SLOT_OPTIONS[0]);
    const [curExtendOption, setCurExtendOption] = useState(EXTEND_OPTIONS[0]);
    const [finalSlotCnt, setFinalSlotCnt] = useState(0);
    const [finalExpireDate, setFinalExpireDate] = useState(null);
    const [cost, setCost] = useState(0);

    const isIncrease = activeOptionTab === "increase";
    const userSlotCnt = user?.userAllowedSlotCnt || 0;
    const slotOptions = getSlotOptions(userSlotCnt, isIncrease);

    const handleSlotOptionChange = (option) => {
        if (option.disabled) return;
        setCurSlotOption(option);
    };

    const handleExtendOptionChange = (option) => {
        setCurExtendOption(option);
    };

    const handleIncreaseBtn = () => {
        if (!user?.userId) return loginAlert("로그인이 필요합니다.");
        if (cost === 0) return alert("결제 금액이 0원입니다. 상품을 다시 선택해주세요.");

        const payUser = {
            userEmail: user.userEmail,
            userNm: user.userNm,
            userPhone: user.userPhone,
        };

        let newTitle = "";
        let extendDays = 0;

        if (
            curSlotOption.value === user.userAllowedSlotCnt &&
            curExtendOption.value !== 0
        ) {
            newTitle = `만료일 연장하기(${curExtendOption.value})`;
        } else {
            if (curSlotOption.value !== user.userAllowedSlotCnt) {
                newTitle = `슬롯 ${curSlotOption.count}개 늘리기`;
                extendDays = Math.max(daysUntilExpire(user.userSlotExpireDate), 0);
            }
            if (curExtendOption.value !== 0) {
                newTitle += " 및 연장하기";
                extendDays += Number(curExtendOption.value);
            }
            if (extendDays > 0) newTitle += `(${extendDays})`;
        }

        const data = {
            prodId: curSlotOption.prodId,
            subProdId: curExtendOption.prodId,
            prodTitle: newTitle,
            prodDisc: 0,
            prodPrice: cost,
        };

        Payment.openPaymentPage(data, payUser, "/order/complete", updateUserInfo);
    };

    const handleDecreaseBtn = async () => {
        if (!user?.userId) return loginAlert("로그인이 필요합니다.");

        if (curSlotOption.value >= user.userAllowedSlotCnt) {
            return alert("현재 슬롯보다 적게만 줄일 수 있습니다.");
        }

        if (curSlotOption.value < user.userUsedSlotCnt) {
            return alert("사용중인 슬롯을 줄이려면 먼저 사용중인 슬롯을 삭제해주세요.");
        }

        const confirmed = window.confirm(
            "정말 줄이시겠습니까? 줄이기하신 슬롯은 환불 되지 않습니다."
        );
        if (!confirmed) return;

        const res = await SlotApi.reduceSlotCount(curSlotOption.value);
        if (res.code === "success") return updateUserInfo();
        alert("슬롯 줄이기에 실패하였습니다.");
    };

    useEffect(() => {
        if (!user?.userAllowedSlotCnt) return;
        setCurSlotOption(getDefaultSlotOption(user.userAllowedSlotCnt, isIncrease));
        setCurExtendOption(getDefaultExtendOption(user.userSlotExpireDate));
    }, [user, activeOptionTab]);

    useEffect(() => {
        if (!user?.userAllowedSlotCnt || !curSlotOption || !curExtendOption) return;

        setFinalSlotCnt(curSlotOption.value);
        const curExpireDate = getLatestDate([user.userSlotExpireDate, new Date()]);
        setFinalExpireDate(addDaysToDate(curExpireDate, curExtendOption.value));

        if (!isIncrease) {
            setCost(0);
            return;
        }

        const remainDays = Math.max(daysUntilExpire(user.userSlotExpireDate), 0);
        const userAllowedSlotCnt =
            user.userAllowedSlotCnt < 50 ? 50 : user.userAllowedSlotCnt;
        const curPrice =
            SLOT_OPTIONS.find((o) => o.value === userAllowedSlotCnt)?.price || 0;
        const newPrice = curSlotOption.price;
        let newCost = (remainDays / 30) * (newPrice - curPrice);
        if (curExtendOption.value > 0) {
            newCost += (curSlotOption.price * curExtendOption.value) / 30;
        }
        setCost(Math.floor(newCost / 100) * 100);
    }, [curSlotOption, curExtendOption, user, isIncrease]);

    const slotsToDelete = Math.max(
        (user?.userUsedSlotCnt || 0) - curSlotOption.value,
        0
    );

    return (
        <>
            <style>{rankTrackDisabledStyles}</style>
            <section className="membership-section membership-option membership-rank-track">
            <div className="membership-banner">
                <div className="membership-banner-content">
                    <div className="membership-banner-text">
                        <h2 className="membership-banner-title">순위 조회 추적 이용권</h2>
                        <p className="membership-banner-description">
                            순위조회 추적 이용권입니다. 무료회원은 3개 무료이용이 가능합니다.
                        </p>
                    </div>
                    <div className="membership-banner-visual" aria-hidden="true">
                        <img src="/static/img/assets/banner_membership.png" alt="" />
                    </div>
                </div>
            </div>

            <div className="membership-option-tabs">
                {OPTION_TABS.map((tab) => (
                    <button
                        key={tab.id}
                        type="button"
                        className={`membership-option-tab${
                            activeOptionTab === tab.id ? " is-active" : ""
                        }`}
                        onClick={() => setActiveOptionTab(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="membership-option-section">
                <h3 className="membership-option-title">슬롯 희망 갯수</h3>
                <div className="membership-option-grid">
                    {slotOptions.map((option) => (
                        <label
                            key={option.value}
                            className={`membership-option-item${
                                option.disabled ? " is-disabled" : ""
                            }`}
                        >
                            <input
                                type="radio"
                                name="slot-count"
                                value={option.value}
                                checked={curSlotOption.value === option.value}
                                disabled={option.disabled}
                                onChange={() => handleSlotOptionChange(option)}
                            />
                            <span className="membership-option-box">
                                <span>슬롯 {option.count}개</span>
                                <strong>{option.priceLabel}</strong>
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {isIncrease && (
                <div className="membership-option-section">
                    <h3 className="membership-option-title">만료일 연장</h3>
                    <div className="membership-option-grid">
                        {EXTEND_OPTIONS.map((option) => (
                            <label key={option.value} className="membership-option-item">
                                <input
                                    type="radio"
                                    name="extend-day"
                                    value={option.value}
                                    checked={curExtendOption.value === option.value}
                                    onChange={() => handleExtendOptionChange(option)}
                                />
                                <span className="membership-option-box membership-option-box--center">
                                    <strong>{option.label}</strong>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            <div className="membership-result">
                <table>
                    <caption>
                        선택한 멤버십 옵션의 최종 슬롯 개수, 최종 만료일, 결제 예상 금액
                    </caption>
                    <thead>
                        <tr>
                            <th>최종 슬롯 개수</th>
                            <th>최종 만료일</th>
                            <th>결제 예상 금액</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{finalSlotCnt}</td>
                            <td>
                                {finalExpireDate
                                    ? formatDateYYYMMDD(finalExpireDate)
                                    : "-"}
                            </td>
                            <td>
                                {isIncrease ? `${cost.toLocaleString()}원` : "-"}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {!isIncrease && slotsToDelete > 0 && (
                <p className="txt18">
                    * 사용슬롯을 줄이시려면 기존에 사용승인 슬롯 {user.userUsedSlotCnt}개에서{" "}
                    {slotsToDelete}개를 삭제하셔야 합니다.
                </p>
            )}
            {!isIncrease && (
                <p className="txt18 mt10">* 줄이기 및 삭제한 슬롯은 복원/환불 되지 않습니다.</p>
            )}

            <div className="btn_area">
                {isIncrease ? (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleIncreaseBtn}
                        disabled={cost === 0}
                    >
                        <span>결제하기</span>
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleDecreaseBtn}
                    >
                        <span>줄이기</span>
                    </button>
                )}
            </div>
        </section>
        </>
    );
};

export default MembershipRankTrack;
